import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { formatErr } from "@/lib/api";
import { Truck, Lock } from "lucide-react";

export default function Login() {
  const { login, login2fa, twoFactor } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      await login(username.trim(), password);
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || ex.message);
    } finally { setLoading(false); }
  };

  const on2fa = async (e) => {
    e?.preventDefault?.();
    setErr(""); setLoading(true);
    try { await login2fa(otp); }
    catch (ex) { setErr(formatErr(ex.response?.data?.detail) || ex.message); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-stretch">
      <div className="hidden lg:flex lg:w-1/2 relative"
           style={{ backgroundImage: "url(https://images.unsplash.com/photo-1785761607935-9640ac2ed483?w=1600&q=80)", backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="absolute inset-0 bg-slate-950/70" />
        <div className="relative z-10 flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-sm flex items-center justify-center">
              <Truck className="w-6 h-6 text-primary-foreground" />
            </div>
            <div className="font-heading font-black text-2xl tracking-tight">AUTOTRANS</div>
          </div>
          <div>
            <div className="text-5xl font-heading font-black leading-tight">Gestione autotrasporti.<br/><span className="text-primary">Multi-azienda, un unico sistema.</span></div>
            <div className="mt-4 text-slate-300 text-base max-w-md">Riconciliazione bancaria, prima nota, conteggi padroncini.</div>
          </div>
          <div className="text-xs text-slate-400 font-mono">© 2026 AUTOTRANS</div>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <Card className="w-full max-w-md p-8 shadow-sm border rounded-md">
          <div className="industrial-strip mb-6 -mx-8 -mt-8" />
          <div className="flex items-center gap-2 mb-6">
            <Lock className="w-5 h-5 text-primary" />
            <h1 className="font-heading font-black text-xl">
              {twoFactor ? "Verifica a due fattori" : "Accedi"}
            </h1>
          </div>
          {!twoFactor ? (
            <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
              <div>
                <Label htmlFor="u" className="text-xs font-heading uppercase tracking-wider">Username o Email</Label>
                <Input id="u" value={username} onChange={(e) => setUsername(e.target.value)}
                       className="h-9 mt-1" required autoFocus
                       data-testid="login-username-input" />
              </div>
              <div>
                <Label htmlFor="p" className="text-xs font-heading uppercase tracking-wider">Password</Label>
                <Input id="p" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                       className="h-9 mt-1" required
                       data-testid="login-password-input" />
              </div>
              {err && <div className="text-sm text-destructive border border-destructive/30 bg-destructive/5 rounded-sm p-2" data-testid="login-error">{err}</div>}
              <Button type="submit" disabled={loading} className="w-full h-10 bg-primary hover:bg-primary/90"
                      data-testid="login-submit-button">
                {loading ? "Accesso..." : "Accedi"}
              </Button>
            </form>
          ) : (
            <form onSubmit={on2fa} className="space-y-4">
              <div className="text-sm text-muted-foreground">Inserisci il codice a 6 cifre dall'app autenticatore.</div>
              <div className="flex justify-center">
                <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
                  <InputOTPGroup>
                    {[0,1,2,3,4,5].map(i => <InputOTPSlot key={i} index={i} />)}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              {err && <div className="text-sm text-destructive" data-testid="otp-error">{err}</div>}
              <Button type="submit" disabled={otp.length < 6 || loading} className="w-full h-10"
                      data-testid="otp-submit-button">Verifica</Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
